import type { PropsWithChildren } from 'react';
import { Checkbox } from '../../../components/Checkbox.tsx';
import {
    togglePersonalCheckbox,
    useCombinedServiceCareRequestedAndEulaAccepted,
} from '../../personal/personalInfoSlice.ts';
import { useAppDispatch } from '../../../configuration/setup/hooks.ts';
import {
    useCompanyCountryCode,
    useCompanyLanguage,
    useIsCompanyCountryCodeInEu27,
} from '../../companyInfo/companyInfoSlice.ts';
import { FormattedMessage } from 'react-intl';
import { CURRENT_EULA_VERSION } from '../eulaConfig.ts';
import { resolveEulaCountry } from '../eulaCountryUtils.ts';
import { makeUrl } from '../../registration/helpers.ts';
import { useRegionWithDefault } from '../../region/regionSlice.ts';

export const CombinedEulaAcceptedAndServiceCareBookedCheckbox = (
    props: PropsWithChildren<{
        disabled?: boolean;
        className?: string;
        error?: string;
    }>
) => {
    const value = useCombinedServiceCareRequestedAndEulaAccepted();
    const isInEu27 = useIsCompanyCountryCodeInEu27();
    const countryCode = useCompanyCountryCode();
    const companyLanguage = useCompanyLanguage();
    const region = useRegionWithDefault();
    const messageIds = isInEu27
        ? [
              'intl-msg:registration.personalData.serviceCareAndEula.1',
              'intl-msg:registration.personalData.serviceCareAndEula.2',
              'intl-msg:registration.personalData.serviceCareAndEula.3',
          ]
        : [
              'intl-msg:registration.personalData.serviceCare.1', //
              'intl-msg:registration.personalData.serviceCare.2',
          ];

    const dispatch = useAppDispatch();
    const onClick = () => dispatch(togglePersonalCheckbox('combinedServiceCareRequestedAndEulaAccepted'));

    const eulaCountry = resolveEulaCountry(countryCode, companyLanguage);

    return (
        <Checkbox
            value={value}
            onClick={onClick}
            disabled={props.disabled}
            className={props.className}
            error={props.error}
        >
            {messageIds.map(id => (
                <div key={id}>
                    <FormattedMessage
                        id={id}
                        values={{
                            b: chunks => <strong>{chunks}</strong>,
                            a1: chunks => (
                                <a
                                    href={makeUrl('serviceTermsServiceCareS', companyLanguage, region)}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                >
                                    {chunks}
                                </a>
                            ),
                            a2: chunks => (
                                <a
                                    href={`https://man-eula.iam.rio.cloud/eula/${CURRENT_EULA_VERSION}/${eulaCountry}.pdf`}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                >
                                    {chunks}
                                </a>
                            ),
                            a3: chunks => (
                                <a
                                    href={makeUrl('serviceSpecsServiceCareS', companyLanguage, region)}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                >
                                    {chunks}
                                </a>
                            ),
                            a4: chunks => (
                                <a
                                    href={'https://www.man.eu/eu-data-act'}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                >
                                    {chunks}
                                </a>
                            ),
                        }}
                    />
                </div>
            ))}
            {props.children}
        </Checkbox>
    );
};
